/**
 * Supplier Integration Component
 *
 * Manage supplier relationships for inventory items with pricing,
 * lead times, and purchase history
 */

import {
  BuildingOfficeIcon,
  CalendarIcon,
  ClockIcon,
  CurrencyDollarIcon,
  PlusIcon,
  StarIcon,
} from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DataTable } from '@/components/ui/data-table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useEnhancedApi } from '@/hooks/use-enhanced-api';
import { useToast } from '@/hooks/use-toast';
import { ActionConfig, DataTableColumn } from '@/types/datatable';
import { InventoryItem } from '@/types/inventory';
import { formatCurrency, formatDate, formatNumber } from '@/utils';

interface SupplierIntegrationProps {
  inventoryItem: InventoryItem;
  onUpdate?: () => void;
  className?: string;
}

interface InventorySupplier {
  _id?: string;
  supplier: string | { _id: string; businessName: string };
  supplierName?: string;
  supplierProductCode?: string;
  isPrimary: boolean;
  costPrice: number;
  leadTimeDays: number;
  minimumOrderQuantity: number;
  qualityRating: number;
  isActive: boolean;
  lastOrderDate?: string;
  totalOrdersCount?: number;
  averageDeliveryTime?: number;
}

interface PurchaseHistoryItem {
  id: string;
  orderId: string;
  supplierId: string;
  supplierName: string;
  quantity: number;
  unitPrice: number;
  totalAmount: number;
  orderDate: string;
  deliveryDate?: string;
  status: 'pending' | 'delivered' | 'cancelled';
  qualityScore?: number;
}

interface SupplierFormData {
  supplierId: string;
  supplierProductCode: string;
  isPrimary: boolean;
  costPrice: number;
  leadTimeDays: number;
  minimumOrderQuantity: number;
  qualityRating: number;
  isActive: boolean;
}

export const SupplierIntegration: React.FC<SupplierIntegrationProps> = ({
  inventoryItem,
  onUpdate,
  className = '',
}) => {
  const { inventory: inventoryApi, suppliers: suppliersApi } = useEnhancedApi();
  const { toast } = useToast();
  const showToast = (message: string, isSuccess: boolean) => {
    toast({
      title: isSuccess ? 'Success' : 'Error',
      description: message,
      variant: isSuccess ? 'default' : 'destructive',
    });
  };
  const [suppliers, setSuppliers] = useState<InventorySupplier[]>(
    (inventoryItem.suppliers as unknown as InventorySupplier[]) || []
  );
  const [availableSuppliers, setAvailableSuppliers] = useState<
    Array<{ _id: string; businessName: string }>
  >([]);
  const [purchaseHistory, setPurchaseHistory] = useState<PurchaseHistoryItem[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] =
    useState<InventorySupplier | null>(null);

  // Form state
  const [supplierForm, setSupplierForm] = useState<SupplierFormData>({
    supplierId: '',
    supplierProductCode: '',
    isPrimary: false,
    costPrice: 0,
    leadTimeDays: 7,
    minimumOrderQuantity: 1,
    qualityRating: 5,
    isActive: true,
  });

  // Load available suppliers
  const loadAvailableSuppliers = async () => {
    try {
      const response = await suppliersApi.getAll({
        limit: 100,
        approvalStatus: 'approved',
      });
      if (response.success && response.data) {
        const suppliersList = Array.isArray(response.data)
          ? response.data
          : response.data.items || [];
        setAvailableSuppliers(suppliersList);
      }
    } catch (error) {}
  };

  // Load purchase history
  const loadPurchaseHistory = async () => {
    try {
      // Mock data for demonstration - replace with actual API call
      const mockHistory: PurchaseHistoryItem[] = [
        {
          id: '1',
          orderId: 'PO-2024-001',
          supplierId: (suppliers[0]?.supplier as string) || '',
          supplierName: 'Green Valley Farms',
          quantity: 100,
          unitPrice: 2.5,
          totalAmount: 250,
          orderDate: '2024-01-15',
          deliveryDate: '2024-01-18',
          status: 'delivered',
          qualityScore: 4.5,
        },
      ];
      setPurchaseHistory(mockHistory);
    } catch (error) {}
  };

  // Calculate supplier statistics
  const supplierStats = useMemo(() => {
    const activeSuppliers = suppliers.filter(s => s.isActive).length;
    const primarySupplier = suppliers.find(s => s.isPrimary);
    const averageQuality =
      suppliers.length > 0
        ? suppliers.reduce((sum, s) => sum + s.qualityRating, 0) /
          suppliers.length
        : 0;
    const averageLeadTime =
      suppliers.length > 0
        ? suppliers.reduce((sum, s) => sum + s.leadTimeDays, 0) /
          suppliers.length
        : 0;
    const totalOrders = purchaseHistory.length;
    const totalValue = purchaseHistory.reduce(
      (sum, p) => sum + p.totalAmount,
      0
    );

    return {
      totalSuppliers: suppliers.length,
      activeSuppliers,
      primarySupplierName: primarySupplier
        ? typeof primarySupplier.supplier === 'object'
          ? primarySupplier.supplier.businessName
          : primarySupplier.supplierName || 'Unknown'
        : 'None',
      averageQuality: Math.round(averageQuality * 10) / 10,
      averageLeadTime: Math.round(averageLeadTime),
      totalOrders,
      totalValue,
    };
  }, [suppliers, purchaseHistory]);

  // Supplier table columns
  const supplierColumns: DataTableColumn<InventorySupplier>[] = [
    {
      id: 'supplier',
      header: 'Supplier',
      accessor: 'supplier',
      cell: ({ row }) => {
        const r: any = row as any;
        const supplierName =
          typeof r.supplier === 'object'
            ? r.supplier.businessName
            : r.supplierName || 'Unknown';

        return (
          <div className='font-medium'>
            <div className='flex items-center gap-2'>
              <BuildingOfficeIcon className='h-4 w-4 text-blue-500' />
              {supplierName}
              {r.isPrimary && (
                <Badge className='bg-green-100 text-green-800'>Primary</Badge>
              )}
            </div>
            {r.supplierProductCode && (
              <div className='text-xs text-gray-500 mt-1'>
                Code: {r.supplierProductCode}
              </div>
            )}
          </div>
        );
      },
    },
    {
      id: 'costPrice',
      header: 'Cost Price',
      accessor: 'costPrice',
      cell: ({ row }) => (
        <div className='flex items-center gap-2'>
          <CurrencyDollarIcon className='h-4 w-4 text-green-500' />
          <span className='font-medium'>
            {formatCurrency((row as any).costPrice)}
          </span>
        </div>
      ),
    },
    {
      id: 'leadTime',
      header: 'Lead Time',
      accessor: 'leadTimeDays',
      cell: ({ row }) => (
        <div className='flex items-center gap-2'>
          <ClockIcon className='h-4 w-4 text-orange-500' />
          <span>{(row as any).leadTimeDays} days</span>
        </div>
      ),
    },
    {
      id: 'moq',
      header: 'Min Order Qty',
      accessor: 'minimumOrderQuantity',
      cell: ({ row }) => (
        <span className='text-gray-900'>
          {formatNumber((row as any).minimumOrderQuantity)}{' '}
          {String(inventoryItem.measurementUnit) || 'units'}
        </span>
      ),
    },
    {
      id: 'quality',
      header: 'Quality Rating',
      accessor: 'qualityRating',
      cell: ({ row }) => (
        <div className='flex items-center gap-1'>
          <StarIcon className='h-4 w-4 text-yellow-500 fill-current' />
          <span className='font-medium'>{(row as any).qualityRating}</span>
          <span className='text-gray-500'>/5</span>
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      accessor: 'isActive',
      cell: ({ row }) => (
        <Badge
          className={
            (row as any).isActive
              ? 'bg-green-100 text-green-800'
              : 'bg-gray-100 text-gray-800'
          }
        >
          {(row as any).isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
  ];

  // Purchase history columns
  const historyColumns: DataTableColumn<PurchaseHistoryItem>[] = [
    {
      id: 'orderDate',
      header: 'Order Date',
      accessor: 'orderDate',
      cell: ({ row }) => (
        <div className='flex items-center gap-2'>
          <CalendarIcon className='h-4 w-4 text-blue-500' />
          {formatDate((row as any).orderDate)}
        </div>
      ),
    },
    {
      id: 'orderId',
      header: 'Order ID',
      accessor: 'orderId',
      cell: ({ row }) => (
        <span className='font-mono text-sm'>{(row as any).orderId}</span>
      ),
    },
    {
      id: 'supplier',
      header: 'Supplier',
      accessor: 'supplierName',
      cell: ({ row }) => (
        <span className='font-medium'>{(row as any).supplierName}</span>
      ),
    },
    {
      id: 'quantity',
      header: 'Quantity',
      accessor: 'quantity',
      cell: ({ row }) => (
        <span>
          {formatNumber((row as any).quantity)}{' '}
          {String(inventoryItem.measurementUnit) || 'units'}
        </span>
      ),
    },
    {
      id: 'unitPrice',
      header: 'Unit Price',
      accessor: 'unitPrice',
      cell: ({ row }) => <span>{formatCurrency((row as any).unitPrice)}</span>,
    },
    {
      id: 'totalAmount',
      header: 'Total Amount',
      accessor: 'totalAmount',
      cell: ({ row }) => (
        <span className='font-medium'>
          {formatCurrency((row as any).totalAmount)}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      accessor: 'status',
      cell: ({ row }) => {
        const statusColors = {
          pending: 'bg-yellow-100 text-yellow-800',
          delivered: 'bg-green-100 text-green-800',
          cancelled: 'bg-red-100 text-red-800',
        } as const;
        const key = ((row as any).status ||
          'pending') as keyof typeof statusColors;
        return (
          <Badge className={statusColors[key]}>
            {(row as any).status.charAt(0).toUpperCase() +
              (row as any).status.slice(1)}
          </Badge>
        );
      },
    },
  ];

  // Reset form
  const resetForm = () => {
    setSupplierForm({
      supplierId: '',
      supplierProductCode: '',
      isPrimary: false,
      costPrice: 0,
      leadTimeDays: 7,
      minimumOrderQuantity: 1,
      qualityRating: 5,
      isActive: true,
    });
    setEditingSupplier(null);
  };

  // Handle supplier form submission
  const handleSupplierSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const selectedSupplier = availableSuppliers.find(
        s => s._id === supplierForm.supplierId
      );
      if (!selectedSupplier) {
        throw new Error('Selected supplier not found');
      }

      const supplierData: InventorySupplier = {
        supplier: supplierForm.supplierId,
        supplierName: selectedSupplier.businessName,
        supplierProductCode: supplierForm.supplierProductCode,
        isPrimary: supplierForm.isPrimary,
        costPrice: supplierForm.costPrice,
        leadTimeDays: supplierForm.leadTimeDays,
        minimumOrderQuantity: supplierForm.minimumOrderQuantity,
        qualityRating: supplierForm.qualityRating,
        isActive: supplierForm.isActive,
      };

      if (editingSupplier) {
        // Update existing supplier
        const updatedSuppliers = suppliers.map(s =>
          s._id === editingSupplier._id
            ? { ...supplierData, _id: editingSupplier._id }
            : s
        );
        setSuppliers(updatedSuppliers);
        showToast('Supplier updated successfully', true);
      } else {
        // Add new supplier
        // If setting as primary, remove primary status from others
        if (supplierForm.isPrimary) {
          const updatedSuppliers = suppliers.map(s => ({
            ...s,
            isPrimary: false,
          }));
          setSuppliers([...updatedSuppliers, supplierData]);
        } else {
          setSuppliers([...suppliers, supplierData]);
        }
        showToast('Supplier added successfully', true);
      }

      // Update inventory item with new supplier data
      await inventoryApi.update(inventoryItem._id!, {
        suppliers: suppliers,
      });

      resetForm();
      setIsSheetOpen(false);
      onUpdate?.();
    } catch (error) {
      showToast('Failed to save supplier', false);
    } finally {
      setLoading(false);
    }
  };

  // Handle edit supplier
  const handleEditSupplier = (supplier: InventorySupplier) => {
    setSupplierForm({
      supplierId:
        typeof supplier.supplier === 'object'
          ? supplier.supplier._id
          : supplier.supplier,
      supplierProductCode: supplier.supplierProductCode || '',
      isPrimary: supplier.isPrimary,
      costPrice: supplier.costPrice,
      leadTimeDays: supplier.leadTimeDays,
      minimumOrderQuantity: supplier.minimumOrderQuantity,
      qualityRating: supplier.qualityRating,
      isActive: supplier.isActive,
    });
    setEditingSupplier(supplier);
    setIsSheetOpen(true);
  };

  // Handle delete supplier
  const handleDeleteSupplier = async (supplier: InventorySupplier) => {
    if (!confirm(`Remove ${supplier.supplierName} from this product?`)) {
      return;
    }

    try {
      const updatedSuppliers = suppliers.filter(s => s._id !== supplier._id);
      setSuppliers(updatedSuppliers);
      showToast('Supplier removed successfully', true);
      onUpdate?.();
    } catch (error) {
      showToast('Failed to remove supplier', false);
    }
  };

  // Supplier row actions
  const supplierRowActions: ActionConfig<InventorySupplier>[] = [
    {
      key: 'edit',
      label: 'Edit Supplier',
      onClick: (row: InventorySupplier) => handleEditSupplier(row),
    },
    {
      key: 'primary',
      label: 'Set as Primary',
      onClick: (row: InventorySupplier) => {
        const updatedSuppliers = suppliers.map(s => ({
          ...s,
          isPrimary: s._id === row._id,
        }));
        setSuppliers(updatedSuppliers);
        showToast('Primary supplier updated', true);
      },
      show: (row: InventorySupplier) => !row.isPrimary,
    },
    {
      key: 'remove',
      label: 'Remove Supplier',
      onClick: (row: InventorySupplier) => handleDeleteSupplier(row),
      variant: 'destructive',
    },
  ];

  // Initialize data on component mount
  useEffect(() => {
    loadAvailableSuppliers();
    loadPurchaseHistory();
  }, []);

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Supplier Statistics */}
      <div className='grid grid-cols-1 md:grid-cols-6 gap-4'>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>
              Total Suppliers
            </div>
            <div className='text-2xl font-bold'>
              {supplierStats.totalSuppliers}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>
              Active Suppliers
            </div>
            <div className='text-2xl font-bold text-green-600'>
              {supplierStats.activeSuppliers}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>Avg Quality</div>
            <div className='text-2xl font-bold text-yellow-600'>
              {supplierStats.averageQuality}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>
              Avg Lead Time
            </div>
            <div className='text-2xl font-bold text-blue-600'>
              {supplierStats.averageLeadTime}d
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>
              Total Orders
            </div>
            <div className='text-2xl font-bold'>
              {supplierStats.totalOrders}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>Total Value</div>
            <div className='text-2xl font-bold text-green-600'>
              {formatCurrency(supplierStats.totalValue)}
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue='suppliers'>
        <TabsList>
          <TabsTrigger value='suppliers'>Suppliers</TabsTrigger>
          <TabsTrigger value='history'>Purchase History</TabsTrigger>
        </TabsList>

        <TabsContent value='suppliers'>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-4'>
              <CardTitle className='text-lg font-semibold'>
                Product Suppliers
              </CardTitle>
              <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
                <SheetTrigger asChild>
                  <Button
                    onClick={() => {
                      resetForm();
                      loadAvailableSuppliers();
                    }}
                  >
                    <PlusIcon className='h-4 w-4 mr-2' />
                    Add Supplier
                  </Button>
                </SheetTrigger>
                <SheetContent className='w-full sm:max-w-lg'>
                  <SheetHeader>
                    <SheetTitle>
                      {editingSupplier ? 'Edit Supplier' : 'Add New Supplier'}
                    </SheetTitle>
                  </SheetHeader>

                  <form
                    onSubmit={handleSupplierSubmit}
                    className='space-y-4 mt-6'
                  >
                    <div className='space-y-2'>
                      <Label htmlFor='supplier'>Supplier *</Label>
                      <Select
                        value={supplierForm.supplierId}
                        onValueChange={value =>
                          setSupplierForm(prev => ({
                            ...prev,
                            supplierId: value,
                          }))
                        }
                        disabled={!!editingSupplier}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder='Select supplier...' />
                        </SelectTrigger>
                        <SelectContent>
                          {availableSuppliers.map(supplier => (
                            <SelectItem key={supplier._id} value={supplier._id}>
                              {supplier.businessName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className='space-y-2'>
                      <Label htmlFor='supplierProductCode'>
                        Supplier Product Code
                      </Label>
                      <Input
                        id='supplierProductCode'
                        value={supplierForm.supplierProductCode}
                        onChange={e =>
                          setSupplierForm(prev => ({
                            ...prev,
                            supplierProductCode: e.target.value,
                          }))
                        }
                        placeholder='e.g., GVF-TOM-001'
                      />
                    </div>

                    <div className='grid grid-cols-2 gap-4'>
                      <div className='space-y-2'>
                        <Label htmlFor='costPrice'>Cost Price *</Label>
                        <Input
                          id='costPrice'
                          type='number'
                          value={supplierForm.costPrice}
                          onChange={e =>
                            setSupplierForm(prev => ({
                              ...prev,
                              costPrice: parseFloat(e.target.value) || 0,
                            }))
                          }
                          min='0'
                          step='0.01'
                          required
                        />
                      </div>
                      <div className='space-y-2'>
                        <Label htmlFor='leadTime'>Lead Time (days) *</Label>
                        <Input
                          id='leadTime'
                          type='number'
                          value={supplierForm.leadTimeDays}
                          onChange={e =>
                            setSupplierForm(prev => ({
                              ...prev,
                              leadTimeDays: parseInt(e.target.value) || 0,
                            }))
                          }
                          min='0'
                          required
                        />
                      </div>
                    </div>

                    <div className='grid grid-cols-2 gap-4'>
                      <div className='space-y-2'>
                        <Label htmlFor='moq'>Min Order Quantity *</Label>
                        <Input
                          id='moq'
                          type='number'
                          value={supplierForm.minimumOrderQuantity}
                          onChange={e =>
                            setSupplierForm(prev => ({
                              ...prev,
                              minimumOrderQuantity:
                                parseFloat(e.target.value) || 0,
                            }))
                          }
                          min='0'
                          step='0.01'
                          required
                        />
                      </div>
                      <div className='space-y-2'>
                        <Label htmlFor='quality'>Quality Rating *</Label>
                        <Input
                          id='quality'
                          type='number'
                          value={supplierForm.qualityRating}
                          onChange={e =>
                            setSupplierForm(prev => ({
                              ...prev,
                              qualityRating: parseFloat(e.target.value) || 0,
                            }))
                          }
                          min='0'
                          max='5'
                          step='0.1'
                          required
                        />
                      </div>
                    </div>

                    <div className='flex items-center justify-between'>
                      <div className='flex items-center space-x-2'>
                        <Switch
                          id='isPrimary'
                          checked={supplierForm.isPrimary}
                          onCheckedChange={checked =>
                            setSupplierForm(prev => ({
                              ...prev,
                              isPrimary: checked,
                            }))
                          }
                        />
                        <Label htmlFor='isPrimary'>Primary Supplier</Label>
                      </div>
                      <div className='flex items-center space-x-2'>
                        <Switch
                          id='isActive'
                          checked={supplierForm.isActive}
                          onCheckedChange={checked =>
                            setSupplierForm(prev => ({
                              ...prev,
                              isActive: checked,
                            }))
                          }
                        />
                        <Label htmlFor='isActive'>Active</Label>
                      </div>
                    </div>

                    <Separator />

                    <div className='flex justify-end gap-2'>
                      <Button
                        type='button'
                        hierarchy='outlined'
                        onClick={() => setIsSheetOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button type='submit' loading={loading}>
                        {editingSupplier ? 'Update Supplier' : 'Add Supplier'}
                      </Button>
                    </div>
                  </form>
                </SheetContent>
              </Sheet>
            </CardHeader>
            <CardContent>
              <DataTable
                data={suppliers}
                columns={supplierColumns}
                rowActions={supplierRowActions}
                emptyConfig={{
                  title: 'No Suppliers Found',
                  description:
                    'Add suppliers to track pricing, lead times, and purchase history.',
                }}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value='history'>
          <Card>
            <CardHeader>
              <CardTitle className='text-lg font-semibold'>
                Purchase History
              </CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                data={purchaseHistory}
                columns={historyColumns}
                emptyConfig={{
                  title: 'No Purchase History',
                  description:
                    'Purchase history will appear here once orders are placed.',
                }}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
